/**
 * Shared date-range vocabulary for list-page filters. A range is a pair of
 * LOCAL calendar dates (`YYYY-MM-DD`, null = unbounded); `rangeToIso` widens it
 * to the inclusive [start-of-from-day, end-of-to-day] instants the API's
 * `from`/`to` params expect.
 */
export interface DateRangeValue {
  from: string | null;
  to: string | null;
}

export const EMPTY_RANGE: DateRangeValue = { from: null, to: null };

/** Local calendar date as YYYY-MM-DD (no timezone shifting). */
export function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const BANGKOK_TIME_ZONE = 'Asia/Bangkok';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const THAI_MONTHS = [
  'มกราคม',
  'กุมภาพันธ์',
  'มีนาคม',
  'เมษายน',
  'พฤษภาคม',
  'มิถุนายน',
  'กรกฎาคม',
  'สิงหาคม',
  'กันยายน',
  'ตุลาคม',
  'พฤศจิกายน',
  'ธันวาคม',
] as const;

const ENGLISH_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** Strict calendar-day validation for URL-backed date filters. */
export function isIsoDay(value: string | null): value is string {
  if (value === null) return false;
  const match = ISO_DAY.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  );
}

/** Full date used by interactive form controls; the underlying value stays Gregorian ISO. */
export function formatLocalizedFormDate(value: string, language: string): string {
  if (!isIsoDay(value)) return value;
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  const thai = language.startsWith('th');
  const monthName = thai ? THAI_MONTHS[month - 1] : ENGLISH_MONTHS[month - 1];
  return `${day} ${monthName} ${thai ? year + 543 : year}`;
}

/** Localized display for a datetime-local wall clock without timezone conversion. */
export function formatLocalizedFormDateTime(value: string, language: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(value);
  if (!match) return value;
  const [, date, time] = match;
  if (!date || !time || !isIsoDay(date)) return value;
  return `${formatLocalizedFormDate(date, language)}, ${time}`;
}

function bangkokDayStart(value: string, dayOffset = 0): string | undefined {
  if (!isIsoDay(value)) return undefined;
  const match = ISO_DAY.exec(value)!;
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + dayOffset, -7)).toISOString();
}

export interface BangkokDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/** Calendar and clock fields for one instant in the product timezone. */
export function bangkokDateTimeParts(value: string | number | Date): BangkokDateTimeParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BANGKOK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const numberPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: numberPart('year'),
    month: numberPart('month'),
    day: numberPart('day'),
    hour: numberPart('hour'),
    minute: numberPart('minute'),
  };
}

/** Parse a datetime-local wall clock as Asia/Bangkok, independent of browser timezone. */
export function parseBangkokDateTimeLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  const instant = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour) - 7, Number(minute)),
  );
  const actual = bangkokDateTimeParts(instant);
  if (
    actual.year !== Number(year) ||
    actual.month !== Number(month) ||
    actual.day !== Number(day) ||
    actual.hour !== Number(hour) ||
    actual.minute !== Number(minute)
  ) {
    return null;
  }
  return instant;
}

/** Format an instant for a datetime-local control in Asia/Bangkok. */
export function formatBangkokDateTimeLocal(value: string | number | Date): string {
  const { year, month, day, hour, minute } = bangkokDateTimeParts(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/** Next selectable whole Bangkok minute, always strictly after the supplied instant. */
export function nextBangkokMinute(nowMs = Date.now()): string {
  return formatBangkokDateTimeLocal(Math.floor(nowMs / 60_000) * 60_000 + 60_000);
}

/** The quick-pick shortcuts every date-range filter offers (i18n key = range<Key>). */
export const RANGE_SHORTCUTS = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
] as const;
export type RangeShortcut = (typeof RANGE_SHORTCUTS)[number];

export function rangeForShortcut(key: RangeShortcut, now = new Date()): DateRangeValue {
  const current = bangkokDateTimeParts(now);
  const calendarDate = new Date(Date.UTC(current.year, current.month - 1, current.day));
  const calendarDay = (date: Date) => {
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  };
  const day = (offset: number) => {
    const d = new Date(calendarDate);
    d.setUTCDate(calendarDate.getUTCDate() + offset);
    return d;
  };
  switch (key) {
    case 'today':
      return { from: calendarDay(calendarDate), to: calendarDay(calendarDate) };
    case 'yesterday':
      return { from: calendarDay(day(-1)), to: calendarDay(day(-1)) };
    case 'last7':
      return { from: calendarDay(day(-6)), to: calendarDay(calendarDate) };
    case 'last30':
      return { from: calendarDay(day(-29)), to: calendarDay(calendarDate) };
    case 'thisMonth':
      return {
        from: calendarDay(new Date(Date.UTC(current.year, current.month - 1, 1))),
        to: calendarDay(calendarDate),
      };
    case 'lastMonth':
      return {
        from: calendarDay(new Date(Date.UTC(current.year, current.month - 2, 1))),
        to: calendarDay(new Date(Date.UTC(current.year, current.month - 1, 0))),
      };
    case 'thisYear':
      return { from: `${current.year}-01-01`, to: calendarDay(calendarDate) };
    case 'lastYear':
      return {
        from: `${current.year - 1}-01-01`,
        to: `${current.year - 1}-12-31`,
      };
  }
}

/** Write the range onto list-request params as the API's `from`/`to` instants. */
export function setRangeParams(params: URLSearchParams, v: DateRangeValue): void {
  const iso = rangeToIso(v);
  if (iso.from) params.set('from', iso.from);
  if (iso.to) params.set('to', iso.to);
}

/** Set a multi-value filter param as CSV — the mirror of the API's csvParam decoder. */
export function setCsvParam(params: URLSearchParams, key: string, values: string[]): void {
  if (values.length) params.set(key, values.join(','));
}

/** Inclusive instants for the API: from = 00:00:00.000 local, to = 23:59:59.999 local. */
export function rangeToIso(v: DateRangeValue): { from?: string; to?: string } {
  const parse = (s: string) => s.split('-').map(Number) as [number, number, number];
  const out: { from?: string; to?: string } = {};
  if (v.from) {
    const [y, m, d] = parse(v.from);
    out.from = new Date(y, m - 1, d).toISOString();
  }
  if (v.to) {
    const [y, m, d] = parse(v.to);
    out.to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
  }
  return out;
}

/**
 * Half-open local calendar range for ledger endpoints: `from` is inclusive
 * local midnight and `to` is the following local midnight (exclusive).
 */
export function rangeToExclusiveIso(v: DateRangeValue): { from?: string; to?: string } {
  const out: { from?: string; to?: string } = {};
  if (v.from) {
    const from = bangkokDayStart(v.from);
    if (from) out.from = from;
  }
  if (v.to) {
    const to = bangkokDayStart(v.to, 1);
    if (to) out.to = to;
  }
  return out;
}
